// Content area: blog, reviews, Google reviews (list, Takeout upload, manual form),
// FAQs, quiz, job offers, wiki, inspirations (activities), homepage settings.
export const content = {
  shared: {
    seoTitle: "SEO",
    metaTitleLabel: "Meta title",
    metaTitlePlaceholder: "SEO title (max 60 chars)",
    metaDescriptionLabel: "Meta description",
    metaDescriptionPlaceholder: "SEO description (max 160 chars)",
    featuredImageTitle: "Featured image",
    sortOrderHint: "Lower = higher priority",
    titleRequired: "Title is required",
    slugRequired: "Slug is required",
  },
  blog: {
    list: {
      title: "Blog",
      subtitle: "Manage blog posts and articles",
      newPost: "New post",
      searchPlaceholder: "Search posts...",
      category: "Category",
      allCategories: "All categories",
      loadError: "Error loading blog posts. Please try again.",
      empty: "No blog posts found",
      confirmDelete: 'Are you sure you want to delete "{{title}}"? This action cannot be undone.',
      featured: "Featured",
      readTime: "{{minutes}} min",
    },
    categories: {
      travelTips: "Travel Tips",
      culture: "Culture",
      food: "Food & Cuisine",
      nature: "Nature",
      guides: "Guides",
      news: "News",
    },
    form: {
      newTitle: "New blog post",
      editTitle: "Edit blog post",
      basicInfo: "Basic information",
      titlePlaceholder: "e.g. The Ultimate Guide to Vietnamese Street Food",
      slugLabel: "URL slug",
      slugPlaceholder: "e.g. ultimate-vietnamese-street-food-guide",
      categoryLabel: "Category",
      noDestination: "No destination",
      excerptLabel: "Excerpt",
      excerptPlaceholder: "Brief summary for cards and listings (max 500 chars)...",
      contentTitle: "Content",
      contentPlaceholder: "Write your blog post content...",
      galleryTitle: "Photo gallery",
      galleryPositionLabel: "Position:",
      galleryPositionText:
        "Gallery appears as a full-width grid section below the article content and tags, above related trips. Use the Image button in the Content editor toolbar above to insert images inline within the article text.",
      authorTitle: "Author",
      authorNameLabel: "Author name",
      authorNamePlaceholder: "e.g. CK Go2 Team",
      readTimeLabel: "Read time (minutes)",
      readTimePlaceholder: "e.g. 8",
      authorAvatarLabel: "Author avatar",
      relatedTripsTitle: "Related trips",
      relatedTripsHint: "Choose up to {{max}} trips. Selected: {{count}}.",
      relatedTripsLimitReached:
        "Limit of {{max}} related trips reached. Untick one to add another.",
      relatedTripsLimitReachedHidden:
        "Limit of {{max}} related trips reached. Untick one or remove the unpublished ones to add another.",
      relatedTripsHidden_one:
        "{{count}} selected trip is not published and is missing from the list.",
      relatedTripsHidden_few:
        "{{count}} selected trips are not published and are missing from the list.",
      relatedTripsHidden_other:
        "{{count}} selected trips are not published and are missing from the list.",
      relatedTripsRemoveHidden: "Remove unpublished",
      tagsTitle: "Tags",
      tagsPlaceholder: "poznavaci-zajezdy, asie, tipy-na-cestu",
      tagsHint: "Separate with commas",
      publishedAtLabel: "Published at",
      publishedAtHint: "Auto-set when status changes to Published",
      featuredPost: "Featured post",
    },
  },
  reviews: {
    list: {
      title: "Reviews",
      subtitle: "Manage customer reviews",
      newReview: "New review",
      searchPlaceholder: "Search reviews...",
      filterAll: "All",
      filterApproved: "Approved",
      filterPending: "Pending",
      loadError: "Error loading reviews. Please try again.",
      empty: "No reviews found",
      from: "from {{location}}",
      featured: "Featured",
      video: "Video",
      pendingApproval: "Pending approval",
      sourceCustomer: "From customer",
      tripMeta: "Trip: {{title}}",
      guideMeta: "Guide: {{name}}",
      destinationMeta: "Destination: {{name}}",
      tripDateMeta: "Trip date: {{date}}",
      approve: "Approve",
      revokeApproval: "Revoke approval",
      confirmDelete: "Are you sure you want to delete this review?",
    },
    form: {
      newTitle: "New review",
      editTitle: "Edit review",
      newSubtitle: "Create a new review",
      editSubtitle: "Editing review from {{name}}",
      customerInfoTitle: "Customer information",
      customerNameLabel: "Customer name",
      customerNamePlaceholder: "e.g. John Smith",
      customerLocationLabel: "City/Location",
      customerLocationPlaceholder: "e.g. New York",
      contentTitle: "Review content",
      ratingLabel: "Rating",
      ratingOf: "{{rating}} of 5",
      reviewTitleLabel: "Review title",
      reviewTitlePlaceholder: "e.g. Unforgettable experience!",
      reviewTextLabel: "Review text",
      reviewTextPlaceholder: "Write the customer review...",
      tripDateLabel: "Trip date",
      connectionsTitle: "Connections",
      selectTrip: "-- Select trip --",
      guideLabel: "Guide",
      selectGuide: "-- Select guide --",
      selectDestination: "-- Select destination --",
      approvedForDisplay: "Approved for display",
      showAsFeatured: "Show as featured",
      videoTitle: "Video review",
      isVideoLabel: "This is a video review",
      videoUrlLabel: "Video URL",
      photosTitle: "Review photos",
      photosHint: "Upload photos from the customer's trip",
    },
  },
  faqs: {
    list: {
      title: "FAQ",
      subtitle: "Manage frequently asked questions for the website",
      addFaq: "Add FAQ",
      savingOrder: "Saving order...",
      empty: "No FAQ items yet",
      meta: "Order: {{order}} | Status: {{status}}",
      confirmDelete: "Delete this FAQ?",
      moveTopicUp: "Move topic up",
      moveTopicDown: "Move topic down",
    },
    form: {
      newTitle: "New FAQ",
      editTitle: "Edit FAQ",
      newSubtitle: "Create a new FAQ item",
      editSubtitle: "Editing: {{question}}...",
      topicLabel: "Topic",
      topicRequired: "Topic is required",
      topicPlaceholder: "e.g. Booking & Payments",
      questionLabel: "Question",
      questionRequired: "Question is required",
      questionPlaceholder: "e.g. How do I book a trip?",
      answerLabel: "Answer",
      answerRequired: "Answer is required",
      answerPlaceholder: "Write the answer...",
      topicSortOrderLabel: "Topic sort order",
      sortOrderLabel: "Sort order (within topic)",
    },
  },
  quiz: {
    title: "Quiz results",
    subtitle: "View trip quiz results and statistics",
    showStats: "Show stats",
    hideStats: "Hide stats",
    totalQuizzes: "Runs",
    newsletterSignups: "Newsletter signups",
    last30Days: "Last 30 days",
    topTrip: "Top trip",
    mostRecommended: "Most recommended trips",
    mostRecommendedDestinations: "Most recommended countries",
    topDestination: "Top country",
    variantSwitch: "Trip finder variant",
    variantKviz: "Quiz (trips)",
    variantHledac: "Finder (countries)",
    thResult: "Result",
    thClicks: "Clicks",
    answerDistribution: "Answer distribution",
    resultUnavailable: "No longer available",
    clicksCount_one: "{{count}} click",
    clicksCount_few: "{{count}} clicks",
    clicksCount_other: "{{count}} clicks",
    questions: {
      tripType: "Trip type",
      duration: "Duration",
      bestMonth: "Month",
      type: "Type (old quiz)",
      budget: "Budget (old quiz)",
      companion: "Companion (old quiz)",
      region: "Region (old quiz)",
    },
    times_one: "{{count}} time",
    times_few: "{{count}} times",
    times_other: "{{count}} times",
    thAnswers: "Answers",
    thRecommended: "Recommended",
    empty: "No quiz results found",
    confirmDelete: "Are you sure you want to delete this quiz result?",
    subscribedNewsletter: "Subscribed to newsletter",
    showing: "Showing {{from}} to {{to}} of {{total}} results",
    answers: {
      adventure: "Adventure",
      relax: "Relaxation",
      culture: "Culture",
      nature: "Nature",
      solo: "Solo",
      couple: "Couple",
      family: "Family",
      friends: "Friends",
      northern: "Northern Vietnam",
      central: "Central Vietnam",
      southern: "Southern Vietnam",
      combination: "Combination",
    },
  },
  contentPages: {
    list: {
      title: "Pages",
      subtitle: "Standalone text pages linked from trips",
      newPage: "New page",
      searchPlaceholder: "Search by title or address...",
      loadError: "Error loading pages. Please try again.",
      colTitle: "Title",
      colAddress: "Address",
      colStatus: "Status",
      colDestination: "Destination",
      colUpdated: "Last edited",
      edit: "Edit page {{title}}",
      delete: "Delete page {{title}}",
      viewOnWeb: "View page {{title}} on the web",
      confirmDelete: "Delete page {{title}}?",
      emptyTitle: "No pages yet",
      emptyPurpose:
        "A standalone text you link from a trip — e.g. about tailor-made trips to South Korea.",
      emptyWhere:
        "It appears at {{path}} and in the trip detail once you pick it in the Content tab.",
      emptyDraft: "A draft is not public anywhere — only a published page shows on the web.",
      searchEmpty: "Search found nothing",
      clearSearch: "Clear search",
    },
    form: {
      newTitle: "New page",
      editTitle: "Edit page",
      titlePlaceholder: "e.g. Tailor-made trips to South Korea",
      slugLabel: "Address (slug)",
      slugHint: "The page lives at {{path}}",
      perexLabel: "Perex",
      perexPlaceholder: "A short intro shown under the title and in trip links",
      bodyLabel: "Body",
      bodyPlaceholder: "Page text — use H2 headings; three or more add a table of contents",
      featuredImageLabel: "Main photo (optional)",
      destinationLabel: "Destination (optional)",
      noDestination: "No destination",
      destinationHint: "An unpublished destination is not linked on the web.",
      statusHint: "A draft returns 404 on the web and is missing from the sitemap.",
    },
  },
  jobOffers: {
    list: {
      title: "Job offers",
      subtitle: "Recruitment cards shown in the homepage carousel",
      addOffer: "Add job offer",
      empty: "No job offers yet — the homepage section stays hidden until one is published",
      meta: "{{date}} | Order: {{order}} | Status: {{status}}",
      confirmDelete: "Delete this job offer?",
    },
    form: {
      newTitle: "New job offer",
      editTitle: "Edit job offer",
      titlePlaceholder: "e.g. Hledáme průvodce do Vietnamu",
      descriptionRequired: "Description is required",
      descriptionPlaceholder: "Short teaser shown on the card",
      linkLabel: "Link (CTA target)",
      linkPlaceholder: "/kontakt, https://… or mailto:info@ckgo2.com",
      linkHint: 'Where "Mám zájem – zjistit více" leads. Leave empty to hide the button.',
      cardPhotoLabel: "Card photo",
      backgroundLabel: "Section background (optional)",
      backgroundHint: "Falls back to the card photo.",
      publishedDateLabel: "Published date (shown on card)",
    },
  },
  holidays: {
    list: {
      title: "Holidays and school breaks",
      subtitle:
        "Buttons in the /zajezdy filter panel — clicking a holiday pre-fills the visitor's date range",
      addHoliday: "Add holiday",
      empty: "No holidays yet — the filter panel section stays hidden until one is published",
      meta: "Next: {{window}} | Windows: {{windows}} | Order: {{order}} | Status: {{status}}",
      noUpcoming: "no upcoming window",
      confirmDelete: "Delete this holiday?",
    },
    form: {
      newTitle: "New holiday",
      editTitle: "Edit holiday",
      namePlaceholder: "e.g. Velikonoce",
      nameRequired: "Name is required",
      slugHint:
        "Stable URL key. Keep it unchanged for holidays carried over from code — it backs older links (easter, christmas, spring-break, may-holidays, autumn-break, summer-break, other-holidays).",
      sortOrderHint: "Button order in the filter panel, lowest number first.",
      statusHint: "Keep a work-in-progress holiday as a draft — it won't show on the web.",
    },
    windows: {
      title: "Date windows",
      hint: "The days pre-filled into the visitor's date filter. Feel free to shift them beyond the holiday itself — when it falls on a Monday, people leave on Friday. Usually one window per school year; past ones can be deleted.",
      empty: "No window yet — without one the holiday button has nothing to pre-fill.",
      from: "From",
      to: "To",
      add: "Add window",
      preview: "Shown on the button: {{label}}",
      incomplete: "Fill in both dates — a window with only one date cannot be saved.",
    },
  },
  podcastEpisodes: {
    list: {
      title: "Podcasts",
      subtitle:
        "Episodes shown on /podcasty — the page and the menu item stay hidden until one is published",
      addEpisode: "Add episode",
      empty: "No episodes yet — /podcasty and the menu item stay hidden until one is published",
      meta: "{{date}} | {{duration}} | Status: {{status}}",
      confirmDelete: "Delete this episode?",
      fromYoutube: "From playlist",
      manualBadge: "Manually edited",
    },
    form: {
      newTitle: "New episode",
      editTitle: "Edit episode",
      titlePlaceholder: "e.g. Na cestách s Go2: Život na Srí Lance",
      videoUrlLabel: "Video link (YouTube)",
      videoUrlRequired: "Video link is required",
      videoUrlHint: "The player and the episode thumbnail are both derived from this link.",
      thumbnailLabel: "Thumbnail (optional)",
      thumbnailHint: "Empty = the thumbnail is taken straight from YouTube.",
      publishedAtLabel: "Published date (shown on card)",
      durationLabel: "Duration (minutes)",
      syncNote:
        "This episode came from the YouTube playlist. Once you manually change its title, video, date, duration or thumbnail, the sync will never overwrite it again. Changing only the status or the sort order does NOT opt it out of syncing.",
    },
  },
  wiki: {
    categories: {
      general: "General",
      playbook: "Playbook",
      contacts: "Contacts",
      sop: "SOP",
    },
    list: {
      title: "Wiki",
      subtitle: "Internal playbook & knowledge base for standing-in colleagues.",
      newPage: "New page",
      thCategory: "Category",
      empty: "No wiki pages yet.",
      generalFallback: "General",
    },
    form: {
      newTitle: "New wiki page",
      editTitle: "Edit wiki page",
      newSubtitle: "Internal playbook / knowledge base",
      editSubtitle: "Editing: {{title}}",
      titlePlaceholder: "e.g. Hanoi — local contacts & gotchas",
      generalOption: "— General (all) —",
      categoryLabel: "Category",
      contentLabel: "Content",
      contentPlaceholder: "Write the playbook…",
    },
  },
  activities: {
    list: {
      title: "Inspirations",
      subtitle: "Manage inspirations",
      newInspiration: "New inspiration",
      searchPlaceholder: "Search inspirations...",
      allTypes: "All types",
      allStatuses: "All statuses",
      loadError: "Error loading activities. Please try again.",
      empty: "No inspirations found",
      thActivity: "Activity",
      thDuration: "Duration",
      confirmDelete: 'Are you sure you want to delete "{{title}}"? This action cannot be undone.',
    },
    edit: {
      newTitle: "New inspiration",
      editTitle: "Edit inspiration",
      newSubtitle: "Create a new inspiration",
      editSubtitle: "Editing: {{title}}",
      notFound: "Activity not found",
      backToList: "Back to Inspirations",
    },
    form: {
      activityTypes: "Activity types",
      activityTypesHint:
        "Tick every type the inspiration offers. On the website it appears under the chip of each ticked type.",
    },
    tabs: {
      basic: "Basic info",
      description: "Description",
      gallery: "Gallery",
      included: "Included & Tags",
      videos: "Videos & Reels",
      tips: "Tips",
      related: "Related",
    },
    basic: {
      basicInfoTitle: "Basic information",
      titlePlaceholder: "e.g. Ha Long Bay Cruise",
      slugLabel: "URL slug",
      slugPlaceholder: "e.g. ha-long-bay-cruise",
      difficultyLabel: "Difficulty",
      selectDifficulty: "Select difficulty",
      durationLabel: "Duration",
      durationPlaceholder: "e.g. half day, 3 hours, full day",
      locationLabel: "Location",
      locationPlaceholder: "e.g. Ha Long Bay, Quang Ninh",
      heroPlaceholder: "e.g. Ha Long Bay",
      headingPlaceholder: "e.g. Ha Long Bay Cruise – A journey through emerald waters",
      shortDescriptionTitle: "Short description",
      shortDescriptionPlaceholder: "Brief summary under the page heading and on cards...",
      featuredActivity: "Prefer in Favourite inspirations",
    },
    description: {
      title: "Full description",
      placeholder: "Detailed activity description...",
    },
    gallery: {
      title: "Gallery images",
    },
    included: {
      includedTitle: "What's included",
      includedPlaceholder: "e.g. Lunch, Guide, Equipment",
      includedEmpty: "No included items yet",
      addItem: "Add item",
      tagsTitle: "Tags",
      tagsPlaceholder: "e.g. boat, cruise, scenic",
      tagsEmpty: "No tags yet",
      addTag: "Add tag",
    },
    videos: {
      reelsTitle: "Social Reels (Get inspired)",
      reelsDescription: "Add Instagram reels or short video links with thumbnails.",
      videosTitle: "Videos",
      videosDescription: "Add YouTube or other video URLs to showcase this activity.",
      empty: "No videos yet",
      videoTitlePlaceholder: "Video title",
      addVideo: "Add video",
    },
    tips: {
      tipsTitle: "Useful tips",
      prefill: {
        beforeTrip: "Before you go",
        onSite: "On the spot",
        practical: "Practical information",
      },
      testimonialTitle: "Testimonial",
      quoteLabel: "Quote",
      quotePlaceholder: "Customer testimonial quote...",
      authorNameLabel: "Author name",
      authorNamePlaceholder: "e.g. John Smith",
      authorPhotoLabel: "Author photo",
    },
    related: {
      title: "Related inspirations",
      description:
        "Select which inspirations appear in the website's “Popular inspirations” section. If you select none, the website picks them itself — from this inspiration's destination and its places.",
      pickerLabel: "Popular inspirations",
      pickerPlaceholder: "Search inspirations...",
      autoTitle: "What the website shows without a manual selection",
      autoScope: "Picked from {{destination}} and its places.",
      autoLoading: "Loading preview…",
      autoUnsaved: "The automatic-selection preview appears once the inspiration is saved.",
      autoNoDestination:
        "This inspiration has no published destination, so the “Popular inspirations” section will not render at all. Assign a destination in Basic info, or pick inspirations manually.",
      autoEmpty:
        "{{destination}} has no other published inspiration, so the section will not render on the website.",
      autoOverridden: "Your manual selection wins. Without it the website would show:",
      outsideScopeTitle: "Selected inspirations outside {{destination}}",
      outsideScopeText:
        "The section heading on the website names the destination, so inspirations from another country read as a mistake underneath it. Consider removing them.",
    },
    linkedTrips: {
      title: "Trips that include it",
      hint: "On the website: the selected trips appear on the inspiration page in the ‘Trips where you can experience it’ section, before the destination's other trips.",
      pickerLabel: "Selected trips",
      searchPlaceholder: "Search published trips...",
      unpublishedSuffix: "(unpublished)",
      blockedNew: "Links can be added after the first save.",
      loading: "Loading links...",
    },
  },
  homepage: {
    title: "Homepage",
    subtitle: "Edit the hero, monthly hits, and popular destinations",
    heroTitle: "Hero",
    subtitleLabel: "Subtitle",
    backgroundImageLabel: "Background image",
    monthlyHitsTitle: "Travel hits of the month",
    monthlyHitsHint:
      "Order matters — first item appears first. Leave empty to keep the default content.",
    tripOption: "Trip: {{label}}",
    inspirationOption: "Inspiration: {{label}}",
    monthlyHitsLimitReached: "Limit of {{max}} monthly hits reached. Remove one to add another.",
    typeTrip: "trip",
    typeActivity: "activity",
    popularDestinationsTitle: "Most popular destinations",
    popularDestinationsHint: "Order matters. Leave empty to keep the default content.",
    popularDestinationsLimitReached:
      "Limit of {{max}} destinations reached. Remove one to add another.",
    sizeLarge: "large",
    sizeSmall: "small",
    addPlaceholder: "Add…",
    groupCountries: "Countries",
    groupPlaces: "Places",
    draftSuffix: "draft",
    draftWarning:
      "Draft — the card stays hidden on the website until the destination is published.",
    aboutHeroTitle: "About page",
    aboutHeroHint:
      "Hero photo and quote on the /o-nas page. Without a photo the hero block is not rendered at all.",
    aboutHeroImageLabel: "Hero photo",
    aboutHeroQuoteLabel: "Quote",
    aboutHeroQuoteHint:
      "Shown in quotation marks over the photo. An empty quote falls back to the “O nás” heading.",
    tripTypeImagesTitle: "Trip type card photos",
    tripTypeImagesHint:
      "The three cards in the homepage section “Zážitky podle vašich představ”. An empty pick means a card without a photo.",
  },
  promoBar: {
    title: "Promo bar",
    subtitle: "A thin banner above the website header — every visitor sees it on every page.",
    sectionTitle: "Bar content",
    hint: "Off by default — it only shows when enabled and a text is filled in.",
    cacheHint: "A change reaches the website within ~3 minutes (pages cache the settings briefly).",
    enabled: "Show promo bar",
    textLabel: "Text",
    hrefLabel: "Link (optional)",
    hrefHint:
      "Internal path (e.g. /zajezdy) or full https:// URL. Internal paths are validated against the route map.",
    saveFailed: "The promo bar could not be saved.",
    storedInvalid: "Heads up:",
  },
  googleReviews: {
    origin: {
      export: "Export",
      manual: "Manual",
    },
    list: {
      title: "Google reviews",
      count_one: "{{count}} review",
      count_few: "{{count}} reviews",
      count_other: "{{count}} reviews",
      uploadExport: "Upload export",
      addManual: "Add manually",
      filterOrigin: "Origin",
      filterOriginAll: "All origins",
      filterDestination: "Destination",
      filterDestinationAll: "All destinations",
      filterDestinationNone: "No destination",
      colAuthor: "Author",
      colDate: "Date",
      colExcerpt: "Text",
      colDestination: "Destination",
      colOrigin: "Origin",
      colActions: "Actions",
      noDestination: "No destination",
      lockedTitle: "The destination was chosen by the editors; an import will not change it",
      emptyAll: "No Google reviews yet, upload an export or add one manually",
      emptyFiltered: "No Google review matches the filter.",
      loadError: "Google reviews could not be loaded.",
      pagePrev: "Previous",
      pageNext: "Next",
      pageOf: "Page {{page}} of {{total}}",
      actionFixDestination: "Fix destination",
      actionEdit: "Edit",
      actionDelete: "Delete",
      actionRemovePermanently: "Remove permanently",
      deleteTitle: "Delete the manual review?",
      deleteMessage:
        "The manually entered review will be deleted. If it also appears in a later export, the import will add it again.",
      deleteConfirm: "Delete",
      removeTitle: "Remove permanently?",
      removeMessage:
        "The author's name and the review text will disappear. Only an identifier stays, so that the next export upload does not bring the review back. This cannot be undone.",
      removeConfirm: "Remove permanently",
      cancel: "Cancel",
    },
    upload: {
      title: "Upload a Google Takeout export",
      intro:
        "Choose the ZIP downloaded from Google Takeout (Google Business Profile data). If Takeout split the export into several parts, choose all of them at once. The file is read in your browser; only the reviews of the chosen location go to the server, and nothing is saved until you confirm the summary.",
      fileLabel: "Export files (ZIP)",
      reading: "Reading the export…",
      errorNotZip: "The chosen file is not a ZIP archive.",
      errorNotTakeout: "The ZIP does not contain any location reviews.",
      errorUnsupportedZip: "The ZIP is encrypted or uses an unsupported compression method.",
      errorNoReviews: "The locations in the export have no reviews.",
      errorUnexpected: "The export could not be read.",
      expected:
        "I expect a ZIP from Google Takeout with Google Business Profile data, containing a location folder with reviews.json files.",
      fileName: "File: {{name}}",
      pickAnother: "Choose another file",
      chooseLocationTitle: "Choose a location",
      chooseLocationHint:
        "The export contains several locations. Choose the one whose reviews belong to this website.",
      locationReviews_one: "{{count}} review in the export",
      locationReviews_few: "{{count}} reviews in the export",
      locationReviews_other: "{{count}} reviews in the export",
      continue: "Continue",
      previewing: "Calculating the summary…",
      summaryTitle: "Summary before saving",
      confirm: "Confirm and save",
      cancel: "Cancel",
      doneTitle: "Saved",
      close: "Close",
    },
    summary: {
      location: "Location",
      placeId: "Place ID",
      placeIdMissing: "no place identifier",
      exportDate: "Export date",
      exportDateFromFilename: "from the file name",
      exportDateFromUpload: "upload day",
      total: "Reviews in the export",
      score: "Rating",
      distribution: "Distribution",
      starsRow: "{{stars}} ★",
      added: "Will be added",
      changed: "Will change",
      deleted: "Will be deleted",
      manualPaired: "Paired manual reviews",
      manualAmbiguous: "Ambiguous manual reviews (not paired)",
      skippedRemoved: "Skipped (removed permanently)",
      deletedWarning: "Reviews missing from the export will be deleted.",
      resultAdded: "Added",
      resultChanged: "Changed",
      resultDeleted: "Deleted",
      noValue: "—",
    },
    form: {
      newTitle: "New Google review",
      editTitle: "Edit Google review",
      author: "Author",
      date: "Review date",
      text: "Review text",
      textHint: "The author's original text, without the translation Google adds.",
      destination: "Destination",
      destinationNone: "No destination",
      destinationHint:
        "Pre-filled from the text. Choosing a destination yourself locks it, so an export upload will not change it.",
      starsHint: "A manual Google review always has five stars.",
      exportNotEditable:
        "An exported review cannot be edited, only its destination can be fixed in the list.",
      backToList: "Back to Google reviews",
      loadError: "The Google review could not be loaded.",
    },
    destinationDialog: {
      title: "Fix destination: {{author}}",
      label: "Destination",
      none: "No destination",
      hint: "The corrected destination stays even after the next export upload.",
      save: "Save",
      cancel: "Cancel",
    },
  },
};
